import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Box, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AltRouteIcon from '@mui/icons-material/AltRoute';
import BarChartIcon from '@mui/icons-material/BarChart';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import InventoryIcon from '@mui/icons-material/Inventory2Outlined';
import FactCheckIcon from '@mui/icons-material/FactCheckOutlined';
import ListAltIcon from '@mui/icons-material/ListAlt';
import Sidebar from '../Sidebar';
import AppHeader from '../AppHeader';
import WorkspaceSelector from '../WorkspaceSelector';
import { PanelError, PanelSkeleton, SectionCard } from '../Benchmark/BenchmarkPrimitives';
import {
    BreakdownResponse, CaseRow, CaseWorkflowContract, CaseWorkflowQuery, CensusResponse,
    DiffResponse, FilterOptionsResponse, FunnelResponse, caseWorkflowService
} from '../../services/caseWorkflowService';
import CaseWorkflowFilters, { FilterState } from './CaseWorkflowFilters';
import KpiRow from './KpiRow';
import FunnelChart from './FunnelChart';
import BreakdownChart from './BreakdownChart';
import TrendChart from './TrendChart';
import CensusPanel from './CensusPanel';
import DiffRow from './DiffRow';
import CaseListTable from './CaseListTable';
import CaseTimelineDrawer from './CaseTimelineDrawer';
import {
    EMPTY_NO_CASES, EMPTY_PRE_MIGRATION, RECONSTRUCTED_CAPTION, RERUN_CAPTION,
    RETIRED_RECORDS_CAPTION, censusCount
} from './caseWorkflowVocabulary';
import { windowSince } from './caseWorkflowWindows';
import { dsVar, ink } from '../../theme/tokens';

type Bucket = 'day' | 'week' | 'month';

/**
 * The case-workflow dashboard.
 *
 * Answers "what is the workflow doing?" — as against the workflow editor, which
 * answers "what is the workflow?". Reached from the summary band on the project
 * page, carrying whatever predicate the reader clicked.
 *
 * Every panel here is fed from the one filter state, and every number on it
 * links to the case list with that element's own predicate, so the list can
 * never disagree with the figure that opened it.
 */
const CaseWorkflowDashboard: React.FC = () => {
    const { projectId } = useParams<{ projectId: string }>();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const filters: FilterState = useMemo(() => ({
        window: searchParams.get('window') || 'all',
        flow_code: searchParams.get('flow_code') || '',
        run_id: searchParams.get('run_id') || '',
        dimension: searchParams.get('dimension') || 'priority',
        value: searchParams.get('value') || '',
        transition_code: searchParams.get('transition_code') || '',
        workflow_state: searchParams.get('workflow_state') || '',
        // Defaults to the whole population. A screen that opened already
        // narrowed would report a backlog under a total's label.
        case_status: searchParams.get('case_status') || 'all'
    }), [searchParams]);

    /** The query string, handed to the table so its paging is linkable. */
    const urlFilters = useMemo(
        () => Object.fromEntries(searchParams.entries()),
        [searchParams]
    );

    const [bucket, setBucket] = useState<Bucket>('day');
    const [censusHistory, setCensusHistory] = useState(false);
    const [selectedCase, setSelectedCase] = useState<CaseRow | null>(null);

    const [contract, setContract] = useState<CaseWorkflowContract | null>(null);
    const [options, setOptions] = useState<FilterOptionsResponse | null>(null);
    const [funnel, setFunnel] = useState<FunnelResponse | null>(null);
    const [breakdown, setBreakdown] = useState<BreakdownResponse | null>(null);
    const [trend, setTrend] = useState<BreakdownResponse | null>(null);
    const [census, setCensus] = useState<CensusResponse | null>(null);
    const [diff, setDiff] = useState<DiffResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    /** The predicate. Built once and handed to every request, unchanged. */
    const query: CaseWorkflowQuery = useMemo(() => ({
        project_id: projectId,
        since: windowSince(filters.window),
        flow_code: filters.flow_code || undefined,
        run_id: filters.run_id || undefined,
        dimension: filters.value ? filters.dimension : undefined,
        value: filters.value || undefined,
        transition_code: filters.transition_code || undefined,
        workflow_state: filters.workflow_state || undefined,
        // Sent only when it narrows something, so the default query string stays
        // the one the band links to.
        case_status: filters.case_status === 'not_closed' ? 'not_closed' : undefined
    }), [projectId, filters]);

    const patchFilters = useCallback((patch: Partial<FilterState>) => {
        const next = new URLSearchParams(searchParams);
        Object.entries(patch).forEach(([key, value]) => {
            if (value) next.set(key, value); else next.delete(key);
        });
        // Any change to the predicate invalidates the case list's current page:
        // page 3 of the old selection is not page 3 of the new one, and is
        // often past the end of it.
        next.delete('page');
        setSearchParams(next, { replace: true });
    }, [searchParams, setSearchParams]);

    const load = useCallback(async () => {
        if (!projectId) return;
        setLoading(true);
        setError(null);
        try {
            const [contractRes, optionsRes, funnelRes, breakdownRes, trendRes, censusRes, diffRes] =
                await Promise.all([
                    caseWorkflowService.getContract(),
                    caseWorkflowService.getFilterOptions(query),
                    caseWorkflowService.getFunnel(query),
                    caseWorkflowService.getBreakdown({
                        ...query,
                        dimension: filters.dimension,
                        value: undefined,
                        level: 'case',
                        // Priority is read per case so its bars sum to the case
                        // total; every other dimension keeps the contract's
                        // point-in-time reading.
                        value_at: filters.dimension === 'priority' ? 'case_latest' : 'event'
                    }),
                    caseWorkflowService.getBreakdown({
                        ...query, dimension: filters.dimension, value: undefined, bucket, tz: 'UTC'
                    }),
                    // Case grain: a case is a customer and aggregates every alert
                    // raised against them, so a row count reads 1,746 for 39 cases.
                    caseWorkflowService.getCensus(
                        censusHistory
                            ? { ...query, level: 'case', bucket, tz: 'UTC' }
                            : { ...query, level: 'case' }
                    ),
                    caseWorkflowService.getDiff(query)
                ]);
            setContract(contractRes);
            setOptions(optionsRes);
            setFunnel(funnelRes);
            setBreakdown(breakdownRes);
            setTrend(trendRes);
            setCensus(censusRes);
            setDiff(diffRes);
        } catch (err: any) {
            setError(err?.message || 'Could not load case activity.');
        } finally {
            setLoading(false);
        }
    }, [projectId, query, filters.dimension, bucket, censusHistory]);

    useEffect(() => { load(); }, [load]);

    const drawerOpen = Boolean(selectedCase);
    const allUnattributed = Boolean(
        funnel?.stages.length === 1 && funnel.stages[0].transitionCode === 'unattributed'
    );

    return (
        <div className="flex h-screen overflow-hidden bg-page font-sans text-fg">
            <Sidebar activePage="hub" forceCollapsed={drawerOpen} />
            <main className="flex-1 flex flex-col overflow-hidden bg-page">
                <AppHeader left={<WorkspaceSelector currentProjectId={projectId} />} />
                <div className="flex-1 overflow-y-auto px-8 pb-16 text-left">
                    <Box sx={{ maxWidth: '84rem', mx: 'auto' }}>
                        <Box className="flex items-center gap-3 pt-6 pb-2">
                            <button
                                onClick={() => navigate(`/analytics/projects/${projectId}`)}
                                className="bg-transparent border border-line text-fg-soft hover:text-accent-fg hover:border-accent-fg/50 p-2 rounded-full transition-colors cursor-pointer flex items-center justify-center"
                            >
                                <ArrowBackIcon fontSize="small" />
                            </button>
                            <Box>
                                <h2 className="font-mono text-2xl m-0" style={{ color: dsVar('accent-fg') }}>
                                    Case operations
                                </h2>
                                <Typography sx={{ fontSize: '0.8125rem', color: dsVar('fg-2'), mt: 0.25 }}>
                                    What the workflows in this project have done to cases, and the cases behind
                                    every number.
                                </Typography>
                            </Box>
                        </Box>

                        <Box className="sticky top-0 z-10 bg-page py-3 mb-2">
                            <CaseWorkflowFilters
                                state={filters}
                                onChange={patchFilters}
                                options={options}
                                dimensions={contract?.dimensions ?? []}
                            />
                        </Box>

                        {error && <PanelError message={error} onRetry={load} />}

                        {loading && !funnel && <PanelSkeleton height={400} />}

                        {!error && funnel && !funnel.stages.length && (
                            <Typography sx={{ fontSize: '0.875rem', color: dsVar('fg-muted'), mt: 4 }}>
                                {EMPTY_NO_CASES}
                            </Typography>
                        )}

                        {!error && funnel && funnel.stages.length > 0 && (
                            <Box className="flex flex-col gap-6 mt-2">
                                {allUnattributed && (
                                    <Typography sx={{ fontSize: '0.75rem', color: ink('#FBBF24') }}>
                                        {EMPTY_PRE_MIGRATION}
                                    </Typography>
                                )}

                                <SectionCard
                                    title="What happened"
                                    subtitle={RERUN_CAPTION}
                                    icon={<BarChartIcon sx={{ fontSize: 18 }} />}
                                >
                                    <KpiRow
                                        stages={funnel.stages}
                                        onSelect={(card) => patchFilters({ transition_code: card.transitionCode })}
                                    />
                                </SectionCard>

                                <Box className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                                    <SectionCard
                                        title="How cases moved"
                                        subtitle="Each step a case reached, and how long it waited"
                                        icon={<AltRouteIcon sx={{ fontSize: 18 }} />}
                                    >
                                        <FunnelChart
                                            funnel={funnel}
                                            onSelect={(_, to) => patchFilters({ transition_code: to })}
                                        />
                                    </SectionCard>

                                    <SectionCard
                                        title={breakdown ? `By ${breakdown.label.toLowerCase()}` : 'Breakdown'}
                                        subtitle={
                                            // The population is named on the card, not only in the
                                            // filter bar: a narrowed breakdown is indistinguishable
                                            // from the full one once the reader scrolls past the bar.
                                            [
                                                filters.dimension === 'priority'
                                                    ? 'Each case counted once, at the tier it currently carries'
                                                    : null,
                                                breakdown?.caseStatus === 'not_closed'
                                                    ? 'Cases not yet closed only'
                                                    : null
                                            ].filter(Boolean).join(' · ') || undefined
                                        }
                                        icon={<ListAltIcon sx={{ fontSize: 18 }} />}
                                    >
                                        {breakdown && (
                                            <BreakdownChart
                                                data={breakdown}
                                                selected={filters.value || null}
                                                onSelect={(value) => patchFilters({
                                                    dimension: filters.dimension,
                                                    value: value === filters.value ? '' : (value ?? '')
                                                })}
                                            />
                                        )}
                                    </SectionCard>
                                </Box>

                                <SectionCard
                                    title="Over time"
                                    subtitle={trend ? `Split by ${trend.label.toLowerCase()}` : undefined}
                                    icon={<ShowChartIcon sx={{ fontSize: 18 }} />}
                                    actions={
                                        <ToggleButtonGroup
                                            value={bucket}
                                            exclusive
                                            size="small"
                                            onChange={(_, next) => next && setBucket(next)}
                                            sx={{
                                                '.MuiToggleButton-root': {
                                                    fontSize: '0.6875rem', color: dsVar('fg-2'),
                                                    borderColor: dsVar('line'), textTransform: 'none', px: 1.25, py: 0.25
                                                },
                                                '.Mui-selected': { color: `${dsVar('accent-fg')} !important`, backgroundColor: 'rgba(255,222,89,0.1) !important' }
                                            }}
                                        >
                                            <ToggleButton value="day">Daily</ToggleButton>
                                            <ToggleButton value="week">Weekly</ToggleButton>
                                            <ToggleButton value="month">Monthly</ToggleButton>
                                        </ToggleButtonGroup>
                                    }
                                >
                                    {trend && <TrendChart data={trend} />}
                                </SectionCard>

                                <Box className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                                    <SectionCard
                                        title="Backlog"
                                        subtitle="A different question from the counts above"
                                        icon={<InventoryIcon sx={{ fontSize: 18 }} />}
                                        actions={
                                            <ToggleButtonGroup
                                                value={censusHistory ? 'history' : 'now'}
                                                exclusive
                                                size="small"
                                                onChange={(_, next) => next && setCensusHistory(next === 'history')}
                                                sx={{
                                                    '.MuiToggleButton-root': {
                                                        fontSize: '0.6875rem', color: dsVar('fg-2'),
                                                        borderColor: dsVar('line'), textTransform: 'none', px: 1.25, py: 0.25
                                                    },
                                                    '.Mui-selected': { color: `${dsVar('accent-fg')} !important`, backgroundColor: 'rgba(255,222,89,0.1) !important' }
                                                }}
                                            >
                                                <ToggleButton value="now">Now</ToggleButton>
                                                <ToggleButton value="history">Over time</ToggleButton>
                                            </ToggleButtonGroup>
                                        }
                                    >
                                        {census && contract && !censusHistory && (
                                            <CensusPanel
                                                census={census}
                                                states={contract.states}
                                                onSelect={(state) => patchFilters({
                                                    workflow_state: state === filters.workflow_state ? '' : state
                                                })}
                                            />
                                        )}
                                        {census && censusHistory && (
                                            <Box>
                                                <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-2') }}>
                                                    {(census.buckets ?? []).length} points reconstructed from the
                                                    transition log.
                                                </Typography>
                                                {/*
                                                  * Buckets before the log existed under-report, so an
                                                  * uncaptioned chart shows growth that never happened.
                                                  */}
                                                {census.reconstructedSince && (
                                                    <Typography sx={{ fontSize: '0.6875rem', color: ink('#FBBF24'), mt: 1 }}>
                                                        {RECONSTRUCTED_CAPTION(census.reconstructedSince)}
                                                    </Typography>
                                                )}
                                                {/*
                                                  * Stated before the numbers, because it is the
                                                  * whole reason this series sits above the "Now"
                                                  * panel rather than matching it.
                                                  */}
                                                {Boolean(census.recordsNoLongerPresent) && (
                                                    <Typography sx={{ fontSize: '0.6875rem', color: ink('#FBBF24'), mt: 0.5 }}>
                                                        {RETIRED_RECORDS_CAPTION(census.recordsNoLongerPresent as number)}
                                                    </Typography>
                                                )}
                                                <Box className="mt-3 flex flex-col gap-1">
                                                    {(census.buckets ?? []).map((point) => (
                                                        <Box
                                                            key={`${point.at}-${point.state}`}
                                                            className="flex items-baseline justify-between gap-3"
                                                        >
                                                            <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-2') }}>
                                                                {point.at.slice(0, 10)} · {point.state.replace(/_/g, ' ')}
                                                            </Typography>
                                                            <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-soft'), fontVariantNumeric: 'tabular-nums' }}>
                                                                {censusCount(point).toLocaleString()}
                                                            </Typography>
                                                        </Box>
                                                    ))}
                                                </Box>
                                            </Box>
                                        )}
                                    </SectionCard>

                                    <SectionCard
                                        title="Declared versus observed"
                                        subtitle="Whether the documented workflow is the one that ran"
                                        icon={<FactCheckIcon sx={{ fontSize: 18 }} />}
                                    >
                                        {diff && <DiffRow diff={diff} />}
                                    </SectionCard>
                                </Box>

                                <SectionCard
                                    title="The cases behind these numbers"
                                    subtitle="Narrowed by exactly the selection above. Open one for its whole path."
                                    icon={<ListAltIcon sx={{ fontSize: 18 }} />}
                                >
                                    <CaseListTable
                                        query={query}
                                        urlFilters={urlFilters}
                                        coverage={funnel.caseIdCoverage}
                                        onRowSelect={setSelectedCase}
                                        selectedKey={
                                            selectedCase ? `${selectedCase.recordType}:${selectedCase.recordKey}` : null
                                        }
                                    />
                                </SectionCard>
                            </Box>
                        )}
                    </Box>
                </div>
            </main>

            <CaseTimelineDrawer
                open={drawerOpen}
                row={selectedCase}
                query={query}
                onClose={() => setSelectedCase(null)}
            />
        </div>
    );
};

export default CaseWorkflowDashboard;
